import { Injectable, Optional } from '@angular/core';
import { AllowanceFundingFeedbackService } from './allowance-funding-feedback.service';
import { ApiClient } from '../http/api-client';
import { BehaviorSubject } from 'rxjs';
import { BaseResponse } from 'src/app/interfaces/base-response-interface';
import { SendMode } from '../../components/modal/sendfundsdialog/send-funds-dialog.page';
import { TeamMember } from '../../interfaces/team/member/member.interface';
import { SessionService } from '../session/session.service';
import { TabStorageService } from '../tabstorage/tabstorage.service';
import { ManageWellspaceProductSelection } from '../teamcompanies/teamcompanies.service';
// import {LiquidCache} from 'ngx-liquid-cache';

export interface Category {
  taxable: boolean;
  name: string;
  toolTip: string;
  category: string;
}

export interface Team {
  topupDisabled?: boolean;
  id?: string;
  name: string;
  amount?: number;
  proRate?: boolean;
  plan?: string;
  defaultPaymentMethod?: string;
  membersCount?: number;
  teamMembers?: any[];
  metadata?: Record<string, any>;
  benefits?: { prepaid: number; reimbursed: number; welcome: number; birthday: number; anniversary: number };
  autoAddNewMembers?: boolean;
  createdAt: string;
}

export interface UserTeamMembership {
  userId: string;
  teamId: string;
  teamName: string;
  companyId: string | null;
  companyName: string | null;
}

export interface Member {
  id?: string;
  email: string;
}

export interface CreateCompanyScopedPayrollTeamPayload {
  name: string;
  amount?: number;
  metadata?: Record<string, any>;
}

export enum PaymentTargets {
  ALLOWANCES = 'Allowances',
  PLANS = 'Plans',
  REWARDS = 'Rewards',
}

export type SendInviteResponseData = {
  sent: string[];
  failed: string[];
  attemptedCount: number;
  sentCount: number;
  failedCount: number;
  warning?: string;
};

export type InviteAutomationState = {
  enabled: boolean;
  initialInviteSendAt: string | null;
  initialInviteSentAt: string | null;
  lastInviteRunAt: string | null;
  nextRetryAt: string | null;
  disabledAt: string | null;
  initialInviteWaitDays: number;
  retryDelayDays: number;
  pendingInitialCount: number;
  pendingRetryCount: number;
};

export type UpdateInviteAutomationPayload = {
  enabled: boolean;
  initialInviteSendAt?: string | null;
};

export type GetTeamOwnerWallitBalanceResponseData = {
  balance: number;
};

export type CreateOneTimeRewardRecipient = {
  teamUserId: string;
};

export type CreateOneTimeRewardPayload = {
  amount: number;
  destination: 'allowance' | 'wallit';
  message: string;
  note?: string | Record<string, any> | null;
  paymentMethodId: string;
  recipients: CreateOneTimeRewardRecipient[];
};

export type CreateOneTimeRewardResponseData = {
  fundingSource: 'stripe';
  status: 'submitted';
};

export type MonthlyTopupSchedule = {
  fundingActionsEnabled?: boolean;
  fundingStatus?: string;
  fundingAttentionReason?: 'recovery_failed' | 'subscription_canceled' | 'subscription_paused' | 'payment_unresolved' | 'subscription_review' | null;
  configuredMonthlyAmount?: number;
  activeMemberCount?: number;
  monthlyTopupDay: number;
  nextFundingDate: string | null;
  hasSubscription: boolean;
  pending: boolean;
  pendingDay?: number;
};
export type MonthlyTopupSchedulePreview = {
  monthlyTopupDay: number;
  currentFundingDate: string | null;
  nextFundingDate: string;
  transitionAllowanceCents: number;
  transitionFeeCents: number;
  recurringChargeTotalCents: number;
  subscriptionId: string | null;
  retrying: boolean;
  quoteToken: string;
  expiresAt: string;
};

export type MonthlyAllowanceFundingMode = 'prorated' | 'full' | 'none';

export type PreviewMonthlyAllowanceFundingRequest = {
  fundingMode: MonthlyAllowanceFundingMode;
};

export type FundMonthlyAllowanceCandidatesRequest = PreviewMonthlyAllowanceFundingRequest & {
  quoteToken: string;
};

export type PreviewMonthlyAllowanceFundingResponseData = {
  monthlyTopupDay: number;
  nextFundingDate: string;
  candidateCount: number;
  expiresAt: string;
  fundingMode: MonthlyAllowanceFundingMode;
  immediateChargeTotalCents: number;
  immediateFundingSubtotalCents: number;
  immediateServiceFeeCents: number;
  paymentMethod: { last4: string | null; type: string | null };
  quoteToken: string;
  recurringAllowanceSubtotalCents: number;
  recurringChargeTotalCents: number;
  recurringServiceFeeCents: number;
};

export type PreviewMonthlyAllowanceFundingResponse = BaseResponse<
  PreviewMonthlyAllowanceFundingResponseData
>;

export type FundMonthlyAllowanceCandidatesResponseData = {
  candidateCount: number;
  fundingMode: FundMonthlyAllowanceCandidatesRequest['fundingMode'];
  immediateFundingTotalCents: number;
  status: 'submitted';
  subscriptionId: string | null;
};

export type FundMonthlyAllowanceCandidatesResponse = BaseResponse<
  FundMonthlyAllowanceCandidatesResponseData
>;

export type RefundAllowanceResponse = {
  requestId: string;
  memberId: string;
  teamId: string;
  previousBalance?: string;
  balance: string;
  refundedAmount: string;
  status: 'AWAITING_LIQUIDITY' | 'COMPLETED' | 'CANCELLED';
  refundIds?: string[];
  stripeCustomerId?: string;
  stripePaymentMethod?: string;
  code?: 'REFUND_AWAITING_STRIPE_FUNDS';
  availableBalanceCents?: number;
  notificationStatus?: 'sent' | 'failed';
  message?: string;
};

export type TeamMemberRemovalContext = {
  memberId: string;
  teamId: string;
  isLastCompanyTeamMembership: boolean;
};

export type TeamMemberProductEligibilityContext = {
  teamId: string;
  memberId: string;
  companyId: string;
  products: Array<{
    id?: string;
    vendorProductId?: string;
    vendorName?: string;
    productName?: string;
  }>;
  member: { email: string; userId: string | null };
};

export type TeamMemberProductEligibilityRequest = {
  productIds: string[];
  startDate?: string | null;
  endDate?: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class TeamsService {
  // Tracks company-included Plus behavior for teams that should act like Plus
  // without creating a separate Plus subscription charge.
  public static companyPlusPlan = false;
  public static companyId = '';
  public static companyName = '';

  public wellspaceChangedObservable = new BehaviorSubject<any | null | undefined>(undefined);
  private selectedWellspaceValidation?: { teamId: string; promise: Promise<any | null> };
  private selectedWellspaceRequestVersion = 0;

  constructor(private restangular: ApiClient, private sessionService: SessionService,
    @Optional() private fundingFeedback?: AllowanceFundingFeedbackService) {}

  private applyIncludedPlusPlanContext(wellspace: any, company?: any): void {
    TeamsService.companyPlusPlan = true;
    TeamsService.companyId = company?.id || '';
    TeamsService.companyName = company?.name || '';

    if (wellspace && (!wellspace.plan || wellspace.plan === 'Free')) {
      wellspace.plan = 'Plus';
    }
  }

  private getPayrollAdminTeamsConfig(company: any): any {
    const payrollConfig = company?.payrollConfig && typeof company.payrollConfig === 'object'
      ? company.payrollConfig
      : {};
    const nestedPayrollAdminTeamsConfig =
      payrollConfig?.payrollAdminTeams && typeof payrollConfig.payrollAdminTeams === 'object'
        ? payrollConfig.payrollAdminTeams
        : {};
    const payrollAdminTeamsConfig: any = {};

    if (Object.prototype.hasOwnProperty.call(nestedPayrollAdminTeamsConfig, 'defaultPlan')) {
      payrollAdminTeamsConfig.defaultPlan = nestedPayrollAdminTeamsConfig.defaultPlan;
    }

    if (Object.prototype.hasOwnProperty.call(nestedPayrollAdminTeamsConfig, 'plusPlanIncluded')) {
      payrollAdminTeamsConfig.plusPlanIncluded = nestedPayrollAdminTeamsConfig.plusPlanIncluded;
    } else if (Object.prototype.hasOwnProperty.call(payrollConfig, 'plusPlanIncluded')) {
      payrollAdminTeamsConfig.plusPlanIncluded = payrollConfig.plusPlanIncluded;
    }

    return payrollAdminTeamsConfig;
  }

  getCompanyForTeam(teamId: string): Promise<any> {
    return this.restangular.one('teams', 'companies').one('team', teamId).get().toPromise();
  }

  getTeamsForCompany(companyId: string): Promise<any> {
    return this.restangular.one('teams').one('companies', companyId).one('teams').getList().toPromise();
  }

  getPayrollUsersForCompany(companyId: string): Promise<any[]> {
    return this.restangular.one('teams').one('companies', companyId).one('payroll-users').getList().toPromise();
  }

  createCompanyScopedPayrollTeam(
    companyId: string,
    payload: CreateCompanyScopedPayrollTeamPayload,
  ): Promise<{ id: string }> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('teams')
      .customPOST(payload)
      .toPromise();
  }

  setWellspace(wellspace: any): Promise<any | null> {
    const teamId = this.getValidWellspaceId(wellspace);
    if (!teamId) {
      return Promise.resolve(null);
    }

    return this.validateAndSelectWellspace(teamId);
  }

  ensureValidatedSelectedWellspace(): Promise<any | null> {
    if (this.selectedWellspaceValidation) {
      return this.selectedWellspaceValidation.promise;
    }

    const currentWellspace = this.wellspaceChangedObservable.value;
    if (this.getValidWellspaceId(currentWellspace)) {
      return Promise.resolve(currentWellspace);
    }

    const storedWellspace = this.getStoredSelectedWellspace();
    const teamId = this.getValidWellspaceId(storedWellspace);
    if (!teamId) {
      this.clearSelectedWellspace();
      return Promise.resolve(null);
    }

    return this.validateAndSelectWellspace(teamId);
  }

  getSelectedWellspaceCandidateId(): string {
    const currentWellspaceId = this.getValidWellspaceId(this.wellspaceChangedObservable.value);
    if (currentWellspaceId) {
      return currentWellspaceId;
    }

    return this.getValidWellspaceId(this.getStoredSelectedWellspace());
  }

  clearSelectedWellspace(): void {
    this.selectedWellspaceRequestVersion++;
    TabStorageService.removeStorage(TabStorageService.SELECTED_WELLSPACE);
    this.clearDerivedWellspaceContext();
    if (this.wellspaceChangedObservable.value !== null) {
      this.wellspaceChangedObservable.next(null);
    }
  }

  updateCurrentWellspaceStorage(updates: Record<string, any>): void {
    const currentWellspace = this.wellspaceChangedObservable.value;
    if (!this.getValidWellspaceId(currentWellspace)) {
      return;
    }

    Object.assign(currentWellspace, updates);
    TabStorageService.setStorage(TabStorageService.SELECTED_WELLSPACE, currentWellspace);
  }

  private validateAndSelectWellspace(teamId: string): Promise<any | null> {
    if (this.selectedWellspaceValidation?.teamId === teamId) {
      return this.selectedWellspaceValidation.promise;
    }

    const requestVersion = ++this.selectedWellspaceRequestVersion;
    const promise = this.reloadValidatedWellspace(teamId, requestVersion);
    this.selectedWellspaceValidation = { teamId, promise };
    void promise.finally((): void => {
      if (this.selectedWellspaceValidation?.promise === promise) {
        this.selectedWellspaceValidation = undefined;
      }
    });
    return promise;
  }

  private async reloadValidatedWellspace(teamId: string, requestVersion: number): Promise<any | null> {
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      const updatedWellspace = await this.getTeamInfo(teamId);
      if (requestVersion !== this.selectedWellspaceRequestVersion) {
        return null;
      }

      const companyContext = await this.resolveCompanyContext(teamId);
      if (requestVersion !== this.selectedWellspaceRequestVersion) {
        return null;
      }

      this.applyCompanyContext(updatedWellspace, companyContext);
      this.updateValidatedWellspace(updatedWellspace);
      return updatedWellspace;
    } catch (error) {
      if (requestVersion === this.selectedWellspaceRequestVersion) {
        console.error('Error validating selected Wellspace', error);
        this.clearSelectedWellspace();
      }
      return null;
    }
  }

  private async resolveCompanyContext(teamId: string): Promise<{
    companyPlusPlan: boolean;
    companyId: string;
    companyName: string;
  }> {
    if (this.sessionService.isSuperAdmin()) {
      return {
        companyPlusPlan: true,
        companyId: '',
        companyName: '',
      };
    }

    try {
      const company = await this.getCompanyForTeam(teamId);
      return {
        companyPlusPlan: this.getPayrollAdminTeamsConfig(company)?.plusPlanIncluded === true,
        companyId: company?.id || '',
        companyName: company?.name || '',
      };
    } catch (error) {
      console.error('Error loading company context for team', error);
      return {
        companyPlusPlan: false,
        companyId: '',
        companyName: '',
      };
    }
  }

  private applyCompanyContext(
    wellspace: any,
    context: { companyPlusPlan: boolean; companyId: string; companyName: string },
  ): void {
    TeamsService.companyPlusPlan = context.companyPlusPlan;
    TeamsService.companyId = context.companyId;
    TeamsService.companyName = context.companyName;

    if (context.companyPlusPlan) {
      this.applyIncludedPlusPlanContext(wellspace, {
        id: context.companyId,
        name: context.companyName,
      });
    }
  }

  private updateValidatedWellspace(wellspace: any): void {
    TabStorageService.setStorage(TabStorageService.SELECTED_WELLSPACE, wellspace);
    this.wellspaceChangedObservable.next(wellspace);
  }

  private getStoredSelectedWellspace(): any | null {
    const storedWellspace = TabStorageService.getStorage(TabStorageService.SELECTED_WELLSPACE);
    if (!storedWellspace) {
      return null;
    }

    try {
      return JSON.parse(storedWellspace);
    } catch (_) {
      return null;
    }
  }

  private getValidWellspaceId(wellspace: any): string {
    return typeof wellspace?.id === 'string' ? wellspace.id.trim() : '';
  }

  private clearDerivedWellspaceContext(): void {
    TeamsService.companyPlusPlan = false;
    TeamsService.companyId = '';
    TeamsService.companyName = '';
  }

  getUserMemberTeams(userId: string): Promise<any> {
    return this.restangular.one('teams').one('user', userId).one('member').getList().toPromise();
  }

  getUserTeamMemberships(userId: string): Promise<UserTeamMembership[]> {
    return this.restangular.one('teams').one('user', userId).one('memberships').getList().toPromise();
  }

  getUserTeamsInfo(userId: string): Promise<any> {
    return this.restangular.one('teams').one('user', userId).one('all').get().toPromise();
  }

  getUserTeamInfo(userId: string): Promise<any> {
    return this.restangular.one('teams').one('user', userId).get().toPromise();
  }

  getTeamInfo(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).get().toPromise();
  }

  getTeamReconciliation(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('reconciliation').get().toPromise();
  }

  getAllTeams(): Promise<any> {
    return this.restangular.one('teams').get().toPromise();
  }

  getTeamsForBrand(brandSlug: string): Promise<any> {
    return this.restangular.one('teams').one('forbrand', brandSlug).getList().toPromise();
  }

  getCategories(): Promise<[Category]> {
    return this.restangular.one('teams').one('categories').get().toPromise();
  }

  getTeamMembers(teamId: string, wallitBalance?: boolean, includeRemoved?: boolean): Promise<TeamMember[]> {
    return this.restangular
      .one('teams', teamId)
      .all('members')
      .getList({
        ...(wallitBalance ? { wallitBalance: 1 } : {}),
        ...(includeRemoved ? { includeRemoved: 1 } : {}),
      })
      .toPromise();
  }

  getTeamMembersForAllowanceFunding(teamId: string): Promise<TeamMember[]> {
    return this.restangular
      .one('teams', teamId)
      .all('members')
      .getList({
        wallitBalance: 1,
        monthlyAllowanceFunding: 1,
      })
      .toPromise();
  }

  getTeamMembersWithDailyLoginInfo(teamId: string, wallitBalance?: boolean): Promise<TeamMember[]> {
    return this.restangular
      .one('teams', teamId)
      .one('membersWithDailyLoginInfo')
      .getList()
      .toPromise();
  }

  getTeamAnalytics(userId: string, teamId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('user', userId)
      .one('analytics')
      .get()
      .toPromise();
  }

  getTeamMembersLoginInfo(teamId: string, brandId: string, filter: string, page: number, limit: number): Promise<any> {
    return this.restangular
        .one('members')
        .one('logininfo')
        .get({filter, page, limit, teamId, brandId})
        .toPromise();
  }

  getMemberAnalytics(userId: string): Promise<any> {
    return this.restangular
      .one('teams')
      .one('user', userId)
      .one('memberanalytics')
      .get()
      .toPromise();
  }

  getAllTeamsAnalytics(userId: string): Promise<any> {
    return this.restangular.one('teams').one('user', userId).one('analytics').get().toPromise();
  }

  addTeamMember(
    teamId: string,
    userEmail: string,
    userId = '',
    allowance?: number,
    options?: { payrollCompanyMemberSelection?: boolean },
  ): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member')
      .customPOST({
        userId,
        userEmail,
        balance: 0,
        ...(allowance && { monthlyAllowance: allowance }),
        ...(options?.payrollCompanyMemberSelection ? { payrollCompanyMemberSelection: true } : {}),
      })
      .toPromise();
  }

  restoreTeamMember(id: string, teamId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', id)
      .one('restore')
      .customPOST({})
      .toPromise();
  }

  removeTeamMember(id: string, teamId: string, leavingCompany: boolean): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', id)
      .remove({ leavingCompany })
      .toPromise();
  }

  getTeamMemberRemovalContext(id: string, teamId: string): Promise<TeamMemberRemovalContext> {
    return this.restangular
      .one('teams', teamId)
      .one('member', id)
      .one('removal-context')
      .get()
      .toPromise();
  }

  deactivateTeamMemberPendingSettlement(id: string, teamId: string, leavingCompany: boolean): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', id)
      .one('deactivate-pending-settlement')
      .customPOST({ leavingCompany })
      .toPromise();
  }

  finalizeTeamMemberRemoval(id: string, teamId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', id)
      .one('finalize-removal')
      .customPOST({})
      .toPromise();
  }

  createTeam(userId: string, name: string, metadata = {}): Promise<any> {
    return this.restangular
      .one('teams', userId)
      .customPOST({
        name,
        categories: [],
        frequency: 'monthly',
        amount: 0,
        plan: this.sessionService.isSuperAdmin() ? 'Plus' : 'Free',
        metadata,
        ...(TeamsService.companyPlusPlan ? { plusPlanCompany: TeamsService.companyId } : {}),
      })
      .toPromise();
  }

  createTeamAsTeamAdmin(userId: string, name: string, metadata = {}): Promise<any> {
    return this.restangular
      .one('teams')
      .one('team-admin', userId)
      .customPOST({
        name,
        categories: [],
        frequency: 'monthly',
        amount: 0,
        plan: this.sessionService.isSuperAdmin() ? 'Plus' : 'Free',
        metadata,
        ...(TeamsService.companyPlusPlan ? { plusPlanCompany: TeamsService.companyId } : {}),
      })
      .toPromise();
  }

  isTeamAdminWellspaceCreationEnabled(): Promise<boolean> {
    return this.restangular
      .one('teams')
      .one('team-admin')
      .one('wellspace-creation-capability')
      .get()
      .toPromise()
      .then((response: any) => response?.enabled === true);
  }

  modifyTeam(teamId: string, fields: any): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .customPUT({
        ...fields,
      })
      .toPromise().then((response: any) => this.fundingFeedback ? this.fundingFeedback.report(response) : response);
  }

  getTeamProductSelection(teamId: string): Promise<ManageWellspaceProductSelection> {
    return this.restangular
      .one('teams', teamId)
      .one('products')
      .get()
      .toPromise();
  }

  getCompanyScopedTeamProductSelection(
    companyId: string,
    teamId: string,
  ): Promise<ManageWellspaceProductSelection> {
    return this.restangular
      .one('teams')
      .one('companies', companyId)
      .one('teams', teamId)
      .one('products')
      .get()
      .toPromise();
  }

  saveTeamProductSelection(
    teamId: string,
    productIds?: string[],
    allowanceEligibleProductIds?: string[],
  ): Promise<ManageWellspaceProductSelection> {
    const body: any = {};
    if (productIds) {
      body.productIds = productIds;
    }
    if (allowanceEligibleProductIds) {
      body.allowanceEligibleProductIds = allowanceEligibleProductIds;
    }

    return this.restangular
      .one('teams', teamId)
      .one('products')
      .customPUT(body)
      .toPromise();
  }

  resetTeamProductSelection(teamId: string): Promise<ManageWellspaceProductSelection> {
    return this.restangular
      .one('teams', teamId)
      .one('products')
      .one('override')
      .remove()
      .toPromise();
  }

  saveTeamProductAllowanceEligibility(
    teamId: string,
    productId: string,
    allowanceEligible: boolean,
  ): Promise<ManageWellspaceProductSelection> {
    return this.restangular
      .one('teams', teamId)
      .one('products', productId)
      .one('allowance-eligibility')
      .customPUT({ allowanceEligible })
      .toPromise();
  }

  modifyTeamMember(teamId: string, memberId: string, fields: any): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .customPUT({
        ...fields,
      })
      .toPromise();
  }

  getUserEnrollmentWindowContext(userId: string): Promise<{products: any[]; windows: Array<{productId: string; startDate: string; endDate: string}>}> {
    return this.restangular.one('vendors/admin/users', userId).one('enrollment-windows').get().toPromise();
  }

  saveUserEnrollmentWindows(userId: string, request: TeamMemberProductEligibilityRequest): Promise<any> {
    return this.restangular.one('vendors/admin/users', userId).one('enrollment-windows').customPUT(request).toPromise();
  }

  getUserProductEligibilityContext(teamId: string, userId: string, companyId: string): Promise<TeamMemberProductEligibilityContext> {
    return this.restangular.one('teams', teamId).one('user', userId).one('product-eligibility')
      .get({companyId}).toPromise();
  }

  saveUserProductEligibility(teamId: string, userId: string, companyId: string, request: TeamMemberProductEligibilityRequest): Promise<any> {
    return this.restangular.one('teams', teamId).one('user', userId).one('product-eligibility')
      .customPUT({...request, companyId}).toPromise();
  }

  getTeamMemberProductEligibilityContext(
    teamId: string,
    memberId: string,
  ): Promise<TeamMemberProductEligibilityContext> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .one('product-eligibility')
      .get()
      .toPromise();
  }

  saveTeamMemberProductEligibility(
    teamId: string,
    memberId: string,
    request: TeamMemberProductEligibilityRequest,
  ): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .one('product-eligibility')
      .customPUT(request)
      .toPromise();
  }

  refundTeamMemberAllowance(
    teamId: string,
    memberId: string,
    amount: number,
  ): Promise<RefundAllowanceResponse> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .one('allowance-refund')
      .customPOST({ amount })
      .toPromise();
  }

  retryTeamMemberAllowanceRefund(
    teamId: string,
    memberId: string,
    requestId: string,
  ): Promise<RefundAllowanceResponse> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .one('allowance-refund', requestId)
      .one('retry')
      .customPOST({})
      .toPromise();
  }

  cancelTeamMemberAllowanceRefund(
    teamId: string,
    memberId: string,
    requestId: string,
  ): Promise<RefundAllowanceResponse> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .one('allowance-refund', requestId)
      .one('cancel')
      .customPOST({})
      .toPromise();
  }

  deleteTeam(teamId: string, bypassErrors?: boolean): Promise<any> {
    return this.restangular.one('teams', teamId).customDELETE('', { bypassErrors }).toPromise();
  }

  resendEmailToTeamMember(memberId: string, teamId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('member', memberId)
      .one('resend')
      .customPUT()
      .toPromise();
  }

  uploadMemberList(teamId: string, data: any): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('uploadmembers')
      .customPUT(data, undefined, undefined, { 'content-type': 'text/html' })
      .toPromise();
  }

  checkTeamPendingCharges(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('pendingcharges').get().toPromise();
  }

  setBankAccount(publicToken: string, teamId: string, accountId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('setbank', accountId)
      .customPUT({ publicToken })
      .toPromise();
  }

  buildMemberList(members: TeamMember[], team: any): TeamMember[] {
    const newMembers: TeamMember[] = [];
    members.forEach((member: any, index: number) => {
      if (!member.userId || !member.status) {
        if (member.status !== 'active' && team.amount !== 0) {
          newMembers.push(member.email);
        }
      }
      if (member.userId) {
        let me = false;
        if (member.userId === this.sessionService.getUserId()) {
          if (!member.lastName) {
            member.lastName = '';
          }
          if (member.userId === team.ownerUserId) {
            member.lastName += ' (Admin)';
          }
          member.lastName += ' (Me)';
          me = true;
        }
        if (me) {
          const thisMember = members[index];
          members.splice(index, 1);
          members.unshift(thisMember);
        }
      } else {
        member.avatar = 'no_avatar.png';
      }
    });
    return newMembers;
  }

  makeWallitTransfer(
    teamId: string,
    amount: number,
    members: any,
    message: string,
    sendMode: SendMode,
  ): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('makewallittransfer')
      .customPOST({ amount, members, message, sendMode })
      .toPromise();
  }

  /*
  reimburseReceipt(teamId: string, amount: number, fee: number, member: Dictionary<any>, info: any, message: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('reimbursereceipt').customPOST({amount, fee, member, info, message}).toPromise();
  }
   */

  getPaymentMethod(team: any, paymentTarget?: PaymentTargets): Promise<string> {
    return this.getPaymentMethods(team.id).then((methods) => {
      // see if there are any invoice methods available
      const invoiceMethod = methods.find(
        (method: any) =>
          method.bankInfo?.bankName === 'invoice' &&
          method.paymentTargets.indexOf(paymentTarget) >= 0,
      );
      return invoiceMethod || team.defaultPaymentMethod;
    });
  }

  getCustomerId(team: any): Promise<string> {
    return this.getPaymentMethods(team.id).then((methods) => {
      return methods.find(
        (method: { stripePaymentMethod: any }) =>
          team.defaultPaymentMethod === method.stripePaymentMethod,
      )?.stripeCustomerId;
    });
  }

  getPaymentMethods(teamId: string): Promise<any> {
    return this.restangular.one('teams', teamId).one('paymentmethods').getList().toPromise();
  }

  getAvailablePaymentMethods(teamId: string, userId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('users', userId)
      .one('availablePaymentMethodsToAttach')
      .getList()
      .toPromise();
  }

  addInvoiceMethod(teamId: string, paymentTarget: PaymentTargets): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('paymentmethods')
      .customPOST({ paymentTarget })
      .toPromise();
  }

  async removeAvailablePaymentFromTeam(
    teamId: string,
    userId: string,
    paymentMethodId: string,
  ): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('users', userId)
      .one('availablePaymentMethodsToAttach', paymentMethodId)
      .customDELETE()
      .toPromise();
  }

  async attachPaymentToTeam(teamId: string, userId: string, paymentMethodId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('users', userId)
      .one('paymentMethods', paymentMethodId)
      .one('userAcceptedPaymentMethod', 'true')
      .customPUT({})
      .toPromise();
  }

  async attachPaymentToTeamForCompany(
    teamId: string,
    userId: string,
    paymentMethodId: string,
    payload: any = {}
  ): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('users', userId)
      .one('paymentMethods', paymentMethodId)
      .one('userAcceptedPaymentMethodForCompany', 'true')
      .customPUT(payload)
      .toPromise();
  }

  getUserSubscriptionsDate(teamId: string, customerId: string): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('users')
      .one(customerId)
      .one('stripe')
      .one('subscriptions')
      .one('dates')
      .get()
      .toPromise();
  }

  updateTeamProRate(teamId: string, prorate: boolean): Promise<any> {
    return this.restangular
      .one('teams', teamId)
      .one('pro-rate')
      .customPATCH({ prorate })
      .toPromise();
  }

  sendInvites(teamId: string): Promise<BaseResponse<SendInviteResponseData>> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('send-invites')
      .customPOST()
      .toPromise();
  }

  getInviteAutomation(teamId: string): Promise<BaseResponse<InviteAutomationState>> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('invite-automation')
      .customGET()
      .toPromise();
  }

  updateInviteAutomation(
    teamId: string,
    payload: UpdateInviteAutomationPayload,
  ): Promise<BaseResponse<InviteAutomationState>> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('invite-automation')
      .customPUT(payload)
      .toPromise();
  }

  getTeamOwnerWallitBalance(
    teamId: string,
  ): Promise<BaseResponse<GetTeamOwnerWallitBalanceResponseData>> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('owner-balance')
      .customGET()
      .toPromise();
  }

  createOneTimeReward(
    teamId: string,
    payload: CreateOneTimeRewardPayload,
  ): Promise<BaseResponse<CreateOneTimeRewardResponseData>> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('funding')
      .one('one-time-rewards')
      .customPOST(payload)
      .toPromise();
  }

  private monthlyTopupScheduleResource(teamId: string) {
    return this.restangular.one('v5').one('teams', teamId).one('funding').one('monthly-allowances').one('schedule');
  }

  getMonthlyTopupSchedule(teamId: string): Promise<BaseResponse<MonthlyTopupSchedule>> {
    return this.monthlyTopupScheduleResource(teamId).get().toPromise();
  }

  previewMonthlyTopupSchedule(teamId: string, monthlyTopupDay: number): Promise<BaseResponse<MonthlyTopupSchedulePreview>> {
    return this.monthlyTopupScheduleResource(teamId).one('preview').customPOST({ monthlyTopupDay }).toPromise();
  }

  changeMonthlyTopupSchedule(teamId: string, monthlyTopupDay: number, quoteToken: string): Promise<BaseResponse<MonthlyTopupSchedulePreview>> {
    return this.monthlyTopupScheduleResource(teamId).customPUT({ monthlyTopupDay, quoteToken }).toPromise();
  }

  previewAllowanceRecovery(teamId: string): Promise<BaseResponse<any>> {
    return this.restangular.one('v5').one('teams', teamId).one('funding').one('monthly-allowances')
      .one('recovery').one('preview').customPOST({}).toPromise();
  }

  recoverAllowance(teamId: string, quoteToken: string): Promise<BaseResponse<any>> {
    return this.restangular.one('v5').one('teams', teamId).one('funding').one('monthly-allowances')
      .one('recovery').customPOST({quoteToken}).toPromise();
  }

  synchronizeAllowance(teamId: string): Promise<BaseResponse<any>> {
    return this.restangular.one('v5').one('teams', teamId).one('funding').one('monthly-allowances')
      .one('synchronize').customPOST({}).toPromise();
  }

  fundMonthlyAllowanceCandidates(
    teamId: string,
    request: FundMonthlyAllowanceCandidatesRequest,
  ): Promise<FundMonthlyAllowanceCandidatesResponse> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('funding')
      .one('monthly-allowances')
      .customPOST(request)
      .toPromise();
  }

  previewMonthlyAllowanceFunding(
    teamId: string,
    request: PreviewMonthlyAllowanceFundingRequest,
  ): Promise<PreviewMonthlyAllowanceFundingResponse> {
    return this.restangular
      .one('v5')
      .one('teams', teamId)
      .one('funding')
      .one('monthly-allowances')
      .one('preview')
      .customPOST(request)
      .toPromise();
  }
}
